import React, { createContext, useContext, useState } from 'react';
import Notification from '../components/Notification';
import { NotificationType, NewNotification } from '../types/notification-types';
import { NotificationContainer } from '../styles/NotificationContainer';

interface NotificationContext {
  notifications: NotificationType[];
  addNotification: (notification: NewNotification) => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContext | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);

  const addNotification = ({
    type,
    title,
    message,
    link,
    linkText,
  }: NewNotification) => {
    const id = Math.floor(Math.random() * 100000).toString();
    const duration = type === 'success' ? 2000 : 10000;
    setNotifications([
      ...notifications,
      { type, title, message, id, duration, link, linkText },
    ]);
  };

  const removeNotification = (id: string) => {
    setNotifications(
      notifications.filter((notification) => notification.id !== id)
    );
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
      }}
    >
      <NotificationContainer>
        {notifications.map((notification: NotificationType, index: number) => (
          <div key={notification.id}>
            <Notification
              id={notification.id}
              type={notification.type}
              title={notification.title}
              message={notification.message}
              duration={notification.duration}
              link={notification.link}
              linkText={notification.linkText}
            />
          </div>
        ))}
      </NotificationContainer>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
